import React, { useState, useEffect } from 'react';
import {
  Activity, Download, Filter, Search, Calendar, User, Clock,
  AlertCircle, CheckCircle, XCircle, Eye, RefreshCw, ChevronDown,
  ChevronUp, TrendingUp, Users, FileText, Server
} from 'lucide-react';
import './StaffActivity.css';

const api = (path, opts = {}) => {
  const base = '/api';
  return fetch(base + path, {
    credentials: 'include',
    ...opts,
    headers: {
      'Content-Type': 'application/json',
      ...(opts.headers || {})
    }
  });
};

function StaffActivity() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);

  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [actionFilter, setActionFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // all, success, error

  // UI states
  const [showFilters, setShowFilters] = useState(false);
  const [expandedLog, setExpandedLog] = useState(null);

  useEffect(() => {
    loadLogs();
    loadStats();
  }, [page, limit]);

  const loadLogs = async () => {
    try {
      setLoading(true);
      setError('');

      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString()
      });

      if (searchTerm) params.append('search', searchTerm);
      if (actionFilter) params.append('action', actionFilter);
      if (resourceFilter) params.append('resource', resourceFilter);
      if (roleFilter) params.append('staffRole', roleFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const res = await api(`/admin/audit-logs?${params.toString()}`);

      if (!res.ok) {
        throw new Error('Failed to fetch audit logs');
      }

      const data = await res.json();
      setLogs(data.logs || []);

      if (data.pagination) {
        setTotal(data.pagination.total);
        setPages(data.pagination.pages);
      }
    } catch (err) {
      console.error('Error loading logs:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const res = await api(`/admin/audit-logs/stats?${params.toString()}`);

      if (res.ok) {
        const data = await res.json();
        setStats(data.stats);
      }
    } catch (err) {
      console.error('Error loading stats:', err);
    }
  };

  const handleSearch = () => {
    setPage(1);
    loadLogs();
    loadStats();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setActionFilter('');
    setResourceFilter('');
    setRoleFilter('');
    setStartDate('');
    setEndDate('');
    setStatusFilter('');
    setPage(1);
    setTimeout(() => {
      loadLogs();
      loadStats();
    }, 100);
  };

  const handleExport = async (format = 'csv') => {
    try {
      setExporting(true);

      const params = new URLSearchParams({ format });
      if (searchTerm) params.append('search', searchTerm);
      if (actionFilter) params.append('action', actionFilter);
      if (resourceFilter) params.append('resource', resourceFilter);
      if (roleFilter) params.append('staffRole', roleFilter);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const res = await api(`/admin/audit-logs/export?${params.toString()}`);

      if (!res.ok) {
        throw new Error('Export failed');
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit-logs-${Date.now()}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export logs');
    } finally {
      setExporting(false);
    }
  };

  const getActionIcon = (statusCode) => {
    if (statusCode >= 200 && statusCode < 300) {
      return <CheckCircle size={16} className="status-icon success" />;
    } else if (statusCode >= 400) {
      return <XCircle size={16} className="status-icon error" />;
    }
    return <AlertCircle size={16} className="status-icon warning" />;
  };

  const getActionColor = (action) => {
    if (action.includes('read')) return 'info';
    if (action.includes('write') || action.includes('create')) return 'success';
    if (action.includes('update')) return 'warning';
    if (action.includes('delete')) return 'danger';
    if (action.includes('login')) return 'primary';
    if (action.includes('logout')) return 'secondary';
    return 'default';
  };

  const getRoleColor = (role) => {
    const roleMap = {
      'ADMIN': 'admin',
      'TECH_SUPPORT': 'tech',
      'MARKETING': 'marketing',
      'ACCOUNTING': 'accounting',
      'CUSTOMER_SUPPORT': 'support'
    };
    return roleMap[role] || 'default';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(2)}s`;
  };

  const filteredLogs = statusFilter
    ? logs.filter(log => {
        if (statusFilter === 'success') return log.statusCode >= 200 && log.statusCode < 300;
        if (statusFilter === 'error') return log.statusCode >= 400;
        return true;
      })
    : logs;

  return (
    <div className="staff-activity">
      <div className="page-header">
        <div className="header-left">
          <Activity size={28} />
          <div>
            <h1>Staff Activity Logs</h1>
            <p className="subtitle">Comprehensive audit trail for transparency and compliance</p>
          </div>
        </div>
        <div className="header-actions">
          <button
            className="btn btn-secondary"
            onClick={() => { loadLogs(); loadStats(); }}
            disabled={loading}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handleExport('csv')}
            disabled={exporting}
          >
            <Download size={16} />
            Export CSV
          </button>
          <button
            className="btn btn-secondary"
            onClick={() => handleExport('json')}
            disabled={exporting}
          >
            <Download size={16} />
            Export JSON
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon total">
              <FileText size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Total Logs</div>
              <div className="stat-value">{stats.totalLogs.toLocaleString()}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon error">
              <AlertCircle size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Error Logs</div>
              <div className="stat-value">{stats.errorLogs.toLocaleString()}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon users">
              <Users size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Unique Users</div>
              <div className="stat-value">{stats.uniqueUsers}</div>
            </div>
          </div>
          <div className="stat-card">
            <div className="stat-icon activity">
              <TrendingUp size={24} />
            </div>
            <div className="stat-content">
              <div className="stat-label">Success Rate</div>
              <div className="stat-value">
                {((stats.totalLogs - stats.errorLogs) / stats.totalLogs * 100).toFixed(1)}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="filters-section">
        <div className="filters-header">
          <button
            className="btn btn-ghost"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
            {showFilters ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {(searchTerm || actionFilter || resourceFilter || roleFilter || startDate || endDate) && (
            <button className="btn btn-ghost" onClick={handleClearFilters}>
              Clear Filters
            </button>
          )}
        </div>

        {showFilters && (
          <div className="filters-content">
            <div className="filter-row">
              <div className="filter-group">
                <label>Search</label>
                <div className="search-input">
                  <Search size={16} />
                  <input
                    type="text"
                    placeholder="Search by email, action, resource, IP..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>
              </div>
            </div>

            <div className="filter-row">
              <div className="filter-group">
                <label>Action</label>
                <input
                  type="text"
                  placeholder="e.g., merchants:read"
                  value={actionFilter}
                  onChange={(e) => setActionFilter(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>Resource</label>
                <input
                  type="text"
                  placeholder="e.g., merchants"
                  value={resourceFilter}
                  onChange={(e) => setResourceFilter(e.target.value)}
                />
              </div>

              <div className="filter-group">
                <label>Staff Role</label>
                <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                  <option value="">All Roles</option>
                  <option value="ADMIN">Admin</option>
                  <option value="TECH_SUPPORT">Tech Support</option>
                  <option value="MARKETING">Marketing</option>
                  <option value="ACCOUNTING">Accounting</option>
                  <option value="CUSTOMER_SUPPORT">Customer Support</option>
                </select>
              </div>

              <div className="filter-group">
                <label>Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                  <option value="">All</option>
                  <option value="success">Success (2xx)</option>
                  <option value="error">Error (4xx, 5xx)</option>
                </select>
              </div>
            </div>

            <div className="filter-row">
              <div className="filter-group">
                <label>Start Date</label>
                <div className="date-input">
                  <Calendar size={16} />
                  <input
                    type="datetime-local"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="filter-group">
                <label>End Date</label>
                <div className="date-input">
                  <Calendar size={16} />
                  <input
                    type="datetime-local"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </div>
              </div>

              <div className="filter-group filter-actions">
                <button className="btn btn-primary" onClick={handleSearch}>
                  <Search size={16} />
                  Apply Filters
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Logs Table */}
      <div className="logs-container">
        {loading ? (
          <div className="loading-state">
            <div className="spinner"></div>
            <p>Loading audit logs...</p>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="empty-state">
            <Activity size={48} />
            <h3>No audit logs found</h3>
            <p>Try adjusting your filters or date range</p>
          </div>
        ) : (
          <>
            <div className="logs-table">
              {filteredLogs.map((log) => (
                <div key={log.id} className="log-item">
                  <div className="log-main">
                    <div className="log-status">
                      {getActionIcon(log.statusCode)}
                    </div>

                    <div className="log-content">
                      <div className="log-header">
                        <div className="log-action">
                          <span className={`badge badge-${getActionColor(log.action)}`}>
                            {log.action}
                          </span>
                          {log.resource && (
                            <span className="log-resource">
                              <Server size={14} />
                              {log.resource}
                            </span>
                          )}
                        </div>
                        <div className="log-meta">
                          <span className="log-time">
                            <Clock size={14} />
                            {formatDate(log.createdAt)}
                          </span>
                          <span className="log-duration">
                            {formatDuration(log.duration)}
                          </span>
                          <span className={`log-status-code status-${Math.floor(log.statusCode / 100)}xx`}>
                            {log.statusCode}
                          </span>
                        </div>
                      </div>

                      <div className="log-details">
                        <div className="log-user">
                          <User size={14} />
                          <span className="user-email">{log.userEmail}</span>
                          {log.staffRole && (
                            <span className={`badge badge-role badge-${getRoleColor(log.staffRole)}`}>
                              {log.staffRole}
                            </span>
                          )}
                        </div>
                        <div className="log-path">
                          <code>{log.method} {log.path}</code>
                        </div>
                      </div>

                      {log.errorMessage && (
                        <div className="log-error">
                          <AlertCircle size={14} />
                          {log.errorMessage}
                        </div>
                      )}

                      {expandedLog === log.id && (
                        <div className="log-expanded">
                          <div className="expanded-section">
                            <strong>Request Details:</strong>
                            <div className="expanded-content">
                              <div><strong>Method:</strong> {log.method}</div>
                              <div><strong>Path:</strong> {log.path}</div>
                              <div><strong>IP Address:</strong> {log.ipAddress}</div>
                              <div><strong>User Agent:</strong> {log.userAgent || 'N/A'}</div>
                              {log.resourceId && <div><strong>Resource ID:</strong> {log.resourceId}</div>}
                            </div>
                          </div>

                          {log.requestBody && (
                            <div className="expanded-section">
                              <strong>Request Body:</strong>
                              <pre className="code-block">
                                {JSON.stringify(log.requestBody, null, 2)}
                              </pre>
                            </div>
                          )}

                          {log.metadata && (
                            <div className="expanded-section">
                              <strong>Metadata:</strong>
                              <pre className="code-block">
                                {JSON.stringify(log.metadata, null, 2)}
                              </pre>
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <button
                      className="btn btn-ghost btn-sm"
                      onClick={() => setExpandedLog(expandedLog === log.id ? null : log.id)}
                    >
                      <Eye size={16} />
                      {expandedLog === log.id ? 'Hide' : 'Details'}
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div className="pagination">
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(Math.max(1, page - 1))}
                  disabled={page === 1}
                >
                  Previous
                </button>
                <div className="pagination-info">
                  Page {page} of {pages} ({total} total logs)
                </div>
                <button
                  className="btn btn-secondary"
                  onClick={() => setPage(Math.min(pages, page + 1))}
                  disabled={page === pages}
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Top Actions */}
      {stats && stats.topActions && stats.topActions.length > 0 && (
        <div className="insights-section">
          <h3>Top Actions</h3>
          <div className="insights-grid">
            {stats.topActions.map((action, idx) => (
              <div key={idx} className="insight-card">
                <div className="insight-label">{action.action}</div>
                <div className="insight-value">{action.count.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top Resources */}
      {stats && stats.topResources && stats.topResources.length > 0 && (
        <div className="insights-section">
          <h3>Most Accessed Resources</h3>
          <div className="insights-grid">
            {stats.topResources.map((resource, idx) => (
              <div key={idx} className="insight-card">
                <div className="insight-label">{resource.resource}</div>
                <div className="insight-value">{resource.count.toLocaleString()}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default StaffActivity;
